// oxlint-disable react/react-compiler -- React Compiler bails out of this file; remove when fixed
import { styled } from "goober";
import { useCallback, useMemo, useRef, useState } from "react";
import type { MonitoringConnectorUsageResponse } from "../../../shared/connectorUsageReport";
import { queries } from "../../../shared/queries";
import type { SpaceMode } from "../../../shared/schema";
import { useQuery } from "../../hooks/useQuery";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { monitoringConnectorUsagePath } from "../../lib/apiPaths";
import { spaceIcon } from "../../lib/spaceIcon";
import { navigate, routes, useCurrentUrl } from "../../lib/urls";
import {
  ConnectorDropdown,
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SectionLabel,
} from "../ConnectorDropdown";
import { ConnectorLogo } from "../ConnectorLogo";
import { createdByName } from "../ConnectorRow";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { useMonitoringInteraction } from "./analytics";
import {
  ConnectorRequestVolumePanel,
  type OverviewStat,
  type RequestVolumeSeries,
} from "./ConnectorRequestVolumePanel";
import { ConnectorBreakdownPanel } from "./ConnectorBreakdownPanel";
import {
  ConnectorVolumeBars,
  type ConnectorVolumeBar,
} from "./ConnectorVolumeBars";
import {
  computeConnectorSummaries,
  computeDailyVolume,
  computeTrafficOverview,
  computeWorkflowSummaries,
  connectorUsageSpaceIds,
  filterUsageRows,
  formatErrorRatePercent,
  formatRequestCount,
  NO_SPACE,
} from "./connectorTraffic";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import { SPACE_COLORS } from "./monitoringPanelLayout";
import { foldsPersonal, PERSONAL_BUCKET } from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import { RefreshButton } from "./RefreshButton";
import { ConnectorsGrid, Content } from "./workflowsLayout";

const OTHER_CONNECTOR = "__other__";

// Gray is the palette's reserved "other/none", so it carries both the folded
// connector tail and usage with no space.
const UNATTRIBUTED_COLOR = "var(--gray-600)";

const DAY = 86_400_000;
const dayFloor = (t: number) => Math.floor(t / DAY) * DAY;

// Windows are calendar ranges keyed by their start; some (this week/month)
// extend past today, which the chart draws as a dashed projection. The endpoint
// takes the resolved start/end day boundaries.
type WindowOption = { value: string; label: string; phrase: string };

const WINDOW_OPTIONS: WindowOption[] = [
  { value: "7", label: "7 days", phrase: "in the last 7 days" },
  { value: "30", label: "30 days", phrase: "in the last 30 days" },
  { value: "this_week", label: "This week", phrase: "this week" },
  { value: "last_week", label: "Last week", phrase: "last week" },
  { value: "this_month", label: "This month", phrase: "this month" },
  { value: "last_month", label: "Last month", phrase: "last month" },
];

const DEFAULT_WINDOW = WINDOW_OPTIONS.find((o) => o.value === "this_week")!;

function windowOption(param: string): WindowOption {
  return (
    WINDOW_OPTIONS.find((option) => option.value === param) ?? DEFAULT_WINDOW
  );
}

// Week starts Monday; all boundaries are UTC to align with the day-grained ledger.
function startOfWeek(day0: number): number {
  const sinceMonday = (new Date(day0).getUTCDay() + 6) % 7;
  return day0 - sinceMonday * DAY;
}
function startOfMonth(day0: number): number {
  const d = new Date(day0);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}
function endOfMonth(day0: number): number {
  const d = new Date(day0);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0);
}

function windowRange(
  value: string,
  today0: number
): { start: number; end: number } {
  switch (value) {
    case "7":
      return { start: today0 - 6 * DAY, end: today0 };
    case "this_week": {
      const s = startOfWeek(today0);
      return { start: s, end: s + 6 * DAY };
    }
    case "last_week": {
      const s = startOfWeek(today0) - 7 * DAY;
      return { start: s, end: s + 6 * DAY };
    }
    case "this_month":
      return { start: startOfMonth(today0), end: endOfMonth(today0) };
    case "last_month": {
      const prev = startOfMonth(today0) - DAY;
      return { start: startOfMonth(prev), end: endOfMonth(prev) };
    }
    default:
      return { start: today0 - 29 * DAY, end: today0 };
  }
}

const PickerTrigger = styled(ControlButton)`
  max-width: 280px;
`;

function LoadingPlaceholder() {
  return (
    <Content aria-hidden>
      <ConnectorsGrid>
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
        <Skeleton
          style={{
            width: "100%",
            height: "100%",
            minHeight: 220,
            borderRadius: 12,
          }}
        />
      </ConnectorsGrid>
    </Content>
  );
}

export function ConnectorsPanel() {
  const recordInteraction = useMonitoringInteraction();
  const displaySpaceName = useSpaceDisplayName();
  const {
    window: windowParam = "this_week",
    space: spaceParam,
    connector: connectorParam,
  } = useCurrentUrl(routes.monitoring.connectors);
  const selectedWindow = windowOption(windowParam);
  const today0 = dayFloor(Date.now());
  const { start, end } = windowRange(selectedWindow.value, today0);

  const [refreshToken, setRefreshToken] = useState(0);
  // Read already collapsed rather than synced row by row, so the window only
  // moves on a refresh.
  const { data: usage, loading: usageLoading } =
    useRevisionedJson<MonitoringConnectorUsageResponse>(
      monitoringConnectorUsagePath(),
      `start=${start}&end=${end}`,
      refreshToken
    );
  // Named for the AI tab but shared by both monitoring tabs so they hydrate the
  // connector list once.
  const [connectors, connectorsQuery] = useQuery(queries.monitorAiConnectors());
  const [workflows, workflowsQuery] = useQuery(queries.allWorkflows());
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );

  const isLoading =
    usageLoading ||
    connectorsQuery.type !== "complete" ||
    workflowsQuery.type !== "complete" ||
    spacesQuery.type !== "complete";

  // Held so a window change doesn't blank the panels mid-fetch. A failed fetch
  // resolves with no payload, and clearing on that reads as a tenant with no
  // traffic at all.
  const heldDaily = useRef(usage?.daily ?? []);
  const heldWorkflowRows = useRef(usage?.workflows ?? []);
  const heldSources = useRef(usage?.sources ?? []);
  if (!isLoading && usage) {
    heldDaily.current = usage.daily;
    heldWorkflowRows.current = usage.workflows;
    heldSources.current = usage.sources;
  }
  const allTrafficRows = heldDaily.current;
  const allWorkflowRows = heldWorkflowRows.current;
  const allSourceRows = heldSources.current;

  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.connectors
  );
  const scopedTrafficRows = useMemo(
    () => allTrafficRows.filter((row) => matchesSpace(row.spaceId)),
    [allTrafficRows, matchesSpace]
  );
  const scopedWorkflowRows = useMemo(
    () => allWorkflowRows.filter((row) => matchesSpace(row.spaceId)),
    [allWorkflowRows, matchesSpace]
  );

  const connectorMeta = useMemo(() => {
    const map = new Map<
      string,
      { name: string; identifier: string; creatorName: string | null }
    >();
    for (const connector of connectors ?? []) {
      map.set(connector.id, {
        name: connector.name,
        identifier: connector.identifier,
        creatorName: createdByName(connector.createdBy),
      });
    }
    return map;
  }, [connectors]);

  const allSummaries = useMemo(
    () => computeConnectorSummaries(scopedTrafficRows),
    [scopedTrafficRows]
  );

  const spaceOptions = useMemo(() => {
    const { spaceIds, hasUnattributed } =
      connectorUsageSpaceIds(scopedTrafficRows);
    const present = new Set(spaceIds);
    let hasPersonal = false;
    const options: { id: string; label: string; mode: SpaceMode }[] = [];
    for (const space of spaces ?? []) {
      if (!present.has(space.id) || !matchesSpace(space.id)) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        hasPersonal = true;
        continue;
      }
      options.push({
        id: space.id,
        label: displaySpaceName(space),
        mode: space.mode,
      });
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    if (hasPersonal)
      options.push({
        id: PERSONAL_BUCKET,
        label: "Personal",
        mode: "personal",
      });
    return { options, hasUnattributed };
  }, [
    scopedTrafficRows,
    spaces,
    scope,
    personalIds,
    matchesSpace,
    displaySpaceName,
  ]);

  const spaceValid =
    spaceParam != null &&
    (spaceParam === NO_SPACE
      ? spaceOptions.hasUnattributed
      : spaceOptions.options.some((option) => option.id === spaceParam));
  const effectiveSpaceId = spaceValid ? spaceParam : null;

  const connectorValid =
    connectorParam != null &&
    allSummaries.some((summary) => summary.connectorId === connectorParam);
  const effectiveConnectorId = connectorValid ? connectorParam : null;

  // The synthetic "Personal" bucket (shown only under "both") matches any
  // personal space; every other selection is an exact space id or null.
  const filterForSpace = useCallback(
    <T extends { spaceId: string | null; connectorId: string }>(
      rows: readonly T[],
      connectorId: string | null
    ): readonly T[] =>
      effectiveSpaceId === PERSONAL_BUCKET
        ? filterUsageRows(
            rows.filter((r) => r.spaceId != null && personalIds.has(r.spaceId)),
            null,
            connectorId
          )
        : filterUsageRows(rows, effectiveSpaceId, connectorId),
    [effectiveSpaceId, personalIds]
  );

  const filteredTraffic = useMemo(
    () => filterForSpace(scopedTrafficRows, effectiveConnectorId),
    [filterForSpace, scopedTrafficRows, effectiveConnectorId]
  );
  const summaries = useMemo(
    () => computeConnectorSummaries(filteredTraffic),
    [filteredTraffic]
  );

  const workflowSummaries = useMemo(
    () =>
      computeWorkflowSummaries(
        filterForSpace(scopedWorkflowRows, effectiveConnectorId)
      ),
    [filterForSpace, scopedWorkflowRows, effectiveConnectorId]
  );
  // Deleted workflows are left out so the table falls back to "Deleted
  // workflow" and drops the link.
  const workflowsById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        badgeSeed: string | null;
        pausedAt: number | null;
      }
    >();
    for (const workflow of workflows ?? []) {
      if (!workflow.deletedAt)
        map.set(workflow.id, {
          id: workflow.id,
          name: workflow.name,
          badgeSeed: workflow.badgeSeed,
          pausedAt: workflow.pausedAt,
        });
    }
    return map;
  }, [workflows]);
  const overview = useMemo(
    () => computeTrafficOverview(summaries),
    [summaries]
  );
  // Fill the whole window (end can be in the future for this week/month), so the
  // chart draws the dashed projection past today.
  const volume = useMemo(
    () => computeDailyVolume(filteredTraffic, start, end),
    [filteredTraffic, start, end]
  );

  // Non-workflow usage broken out by source, shown below the workflows in the
  // drill-in. Non-workflow usage belongs to no space, so a space filter excludes
  // it — matching how the connector/workflow totals reconcile.
  const unattributedSources = useMemo(() => {
    if (
      effectiveConnectorId == null ||
      effectiveSpaceId != null ||
      scope !== "both"
    )
      return [];
    return allSourceRows
      .filter((row) => row.connectorId === effectiveConnectorId)
      .map((row) => ({
        source: row.source,
        requests: row.requestCount,
        errors: row.errorCount,
      }))
      .filter((entry) => entry.requests > 0)
      .sort(
        (a, b) => b.requests - a.requests || a.source.localeCompare(b.source)
      );
  }, [allSourceRows, effectiveConnectorId, effectiveSpaceId, scope]);

  const connectorName = useCallback(
    (connectorId: string) =>
      connectorMeta.get(connectorId)?.name ?? "Deleted connector",
    [connectorMeta]
  );

  // The bar chart keeps every connector in the space scope visible (a connector
  // filter fades the unselected rather than dropping them), so it is scoped by
  // space but never by the selected connector.
  const spaceSummaries = useMemo(
    () => computeConnectorSummaries(filterForSpace(scopedTrafficRows, null)),
    [filterForSpace, scopedTrafficRows]
  );

  // One stable color per connector, ranked across the space-scoped set, so a
  // connector reads the same in the bars and the line-chart tooltip.
  const connectorColorById = useMemo(() => {
    const map = new Map<string, string>();
    spaceSummaries.forEach((summary, i) =>
      map.set(summary.connectorId, SPACE_COLORS[i % SPACE_COLORS.length]!)
    );
    return map;
  }, [spaceSummaries]);

  const volumeSeries = useMemo(() => {
    const coloredCount =
      summaries.length > SPACE_COLORS.length
        ? SPACE_COLORS.length - 1
        : summaries.length;
    const series: RequestVolumeSeries[] = [];
    const seriesKeyOf = new Map<string, string>();
    summaries.forEach((summary, i) => {
      if (i < coloredCount) {
        seriesKeyOf.set(summary.connectorId, summary.connectorId);
        series.push({
          key: summary.connectorId,
          label: connectorName(summary.connectorId),
          color: connectorColorById.get(summary.connectorId)!,
        });
      } else {
        seriesKeyOf.set(summary.connectorId, OTHER_CONNECTOR);
      }
    });
    const otherCount = summaries.length - coloredCount;
    if (otherCount > 0) {
      series.push({
        key: OTHER_CONNECTOR,
        label: `${otherCount} other connector${otherCount === 1 ? "" : "s"}`,
        color: UNATTRIBUTED_COLOR,
        isOther: true,
      });
    }
    return { series, seriesKeyOf };
  }, [summaries, connectorName, connectorColorById]);

  // Every connector in the space scope gets a bar (scrolling horizontally once
  // they overflow); a connector filter fades the unselected rather than
  // dropping them.
  const volumeBars = useMemo<ConnectorVolumeBar[]>(
    () =>
      spaceSummaries.map((summary) => ({
        key: summary.connectorId,
        connectorId: summary.connectorId,
        identifier:
          connectorMeta.get(summary.connectorId)?.identifier ?? "unknown",
        label: connectorName(summary.connectorId),
        requests: summary.requests,
        color: connectorColorById.get(summary.connectorId)!,
      })),
    [spaceSummaries, connectorMeta, connectorName, connectorColorById]
  );

  const setParam = (
    key: "window" | "space" | "connector",
    value: string | null,
    interaction:
      | "connectors.window_change"
      | "connectors.space_filter_change"
      | "connectors.connector_filter_change"
  ) => {
    recordInteraction(interaction, { value: value ?? undefined });
    navigate(
      routes.monitoring.connectors,
      { [key]: value ?? "" },
      { replace: true }
    );
  };

  const hasLoadedOnce = useRef(false);
  if (!isLoading) hasLoadedOnce.current = true;

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <MonitoringLayout
        headerItem={
          <MonitoringScopeSelector route={routes.monitoring.connectors} />
        }
      >
        <LoadingPlaceholder />
      </MonitoringLayout>
    );
  }

  // No usage in the selected window renders a realistic, non-animated skeleton
  // of the populated tab (ghost line, bars, and rows) rather than a bare empty
  // card, matching the other monitoring tabs. The window picker stays live so a
  // different range can still surface data.
  const isEmpty = allSummaries.length === 0;

  const selectedCheck = (
    <Icon name="check" size={12} color="var(--text-secondary)" />
  );

  const rowCheck = (
    <span
      style={{
        flexShrink: 0,
        display: "flex",
        marginLeft: "auto",
      }}
    >
      {selectedCheck}
    </span>
  );

  const connectorPicker = (
    <ConnectorDropdown
      searchable={allSummaries.length >= MIN_OPTIONS_FOR_SEARCH}
      searchPlaceholder="Search connectors…"
      opener={
        <PickerTrigger type="button">
          {effectiveConnectorId ? (
            <ConnectorLogo
              template={{
                name: connectorName(effectiveConnectorId),
                identifier:
                  connectorMeta.get(effectiveConnectorId)?.identifier ??
                  "unknown",
              }}
              size={12}
            />
          ) : (
            <Icon name="layers" size={12} />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveConnectorId
              ? connectorName(effectiveConnectorId)
              : "All connectors"}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
    >
      {(query, close) => {
        const q = query.trim().toLowerCase();
        const matches = allSummaries.filter((summary) => {
          if (!q) return true;
          const meta = connectorMeta.get(summary.connectorId);
          return `${connectorName(summary.connectorId)} ${meta?.creatorName ?? ""}`
            .toLowerCase()
            .includes(q);
        });
        return (
          <>
            {!q && (
              <RowButton
                data-nav-row=""
                onClick={() => {
                  setParam(
                    "connector",
                    null,
                    "connectors.connector_filter_change"
                  );
                  close();
                }}
              >
                <Icon name="layers" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    All connectors
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {allSummaries.length}{" "}
                    {allSummaries.length === 1 ? "connector" : "connectors"}
                  </Text>
                </div>
                {effectiveConnectorId == null && rowCheck}
              </RowButton>
            )}
            {matches.length === 0 && q !== "" ? (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No connectors found.
                </Text>
              </SectionLabel>
            ) : (
              matches.map((summary) => {
                const name = connectorName(summary.connectorId);
                const meta = connectorMeta.get(summary.connectorId);
                const creator = meta?.creatorName ?? null;
                return (
                  <RowButton
                    key={summary.connectorId}
                    data-nav-row=""
                    onClick={() => {
                      setParam(
                        "connector",
                        summary.connectorId,
                        "connectors.connector_filter_change"
                      );
                      close();
                    }}
                  >
                    <ConnectorLogo
                      template={{
                        name,
                        identifier: meta?.identifier ?? "unknown",
                      }}
                      size={16}
                    />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        {name}
                      </Text>
                      {creator && (
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                          ellipsis
                        >
                          {creator}
                        </Text>
                      )}
                    </div>
                    {summary.connectorId === effectiveConnectorId && rowCheck}
                  </RowButton>
                );
              })
            )}
          </>
        );
      }}
    </ConnectorDropdown>
  );

  const selectedSpace =
    effectiveSpaceId != null && effectiveSpaceId !== NO_SPACE
      ? spaceOptions.options.find((option) => option.id === effectiveSpaceId)
      : undefined;

  const spacePicker = (
    <ConnectorDropdown
      searchable={
        spaceOptions.options.length + (spaceOptions.hasUnattributed ? 1 : 0) >=
        MIN_OPTIONS_FOR_SEARCH
      }
      searchPlaceholder="Search spaces…"
      opener={
        <PickerTrigger type="button">
          {effectiveSpaceId == null ? (
            <Icon name="layers" size={12} />
          ) : effectiveSpaceId === NO_SPACE ? (
            <Icon name="circle-dashed" size={12} />
          ) : (
            <Icon
              name={spaceIcon(selectedSpace?.mode ?? "personal")}
              size={12}
            />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveSpaceId == null
              ? "All spaces"
              : effectiveSpaceId === NO_SPACE
                ? "Unattributed"
                : (selectedSpace?.label ?? "All spaces")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
    >
      {(query, close) => {
        const q = query.trim().toLowerCase();
        const matches = spaceOptions.options.filter(
          (option) => !q || option.label.toLowerCase().includes(q)
        );
        const showUnattributed =
          spaceOptions.hasUnattributed && (!q || "unattributed".includes(q));
        return (
          <>
            {!q && (
              <RowButton
                data-nav-row=""
                onClick={() => {
                  setParam("space", null, "connectors.space_filter_change");
                  close();
                }}
              >
                <Icon name="layers" size={16} color="var(--text-primary)" />
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <Text variant="text-regular" ellipsis>
                    All spaces
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {spaceOptions.options.length}{" "}
                    {spaceOptions.options.length === 1 ? "space" : "spaces"}
                  </Text>
                </div>
                {effectiveSpaceId == null && rowCheck}
              </RowButton>
            )}
            {matches.length === 0 && !showUnattributed && q !== "" ? (
              <SectionLabel>
                <Text variant="text-small" color="var(--text-secondary)">
                  No spaces found.
                </Text>
              </SectionLabel>
            ) : (
              <>
                {matches.map((option) => (
                  <RowButton
                    key={option.id}
                    data-nav-row=""
                    onClick={() => {
                      setParam(
                        "space",
                        option.id,
                        "connectors.space_filter_change"
                      );
                      close();
                    }}
                  >
                    <Icon name={spaceIcon(option.mode)} size={16} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        {option.label}
                      </Text>
                    </div>
                    {option.id === effectiveSpaceId && rowCheck}
                  </RowButton>
                ))}
                {showUnattributed && (
                  <RowButton
                    data-nav-row=""
                    onClick={() => {
                      setParam(
                        "space",
                        NO_SPACE,
                        "connectors.space_filter_change"
                      );
                      close();
                    }}
                  >
                    <Icon name="circle-dashed" size={16} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                      <Text variant="text-regular" ellipsis>
                        Unattributed
                      </Text>
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        Usage with no space
                      </Text>
                    </div>
                    {effectiveSpaceId === NO_SPACE && rowCheck}
                  </RowButton>
                )}
              </>
            )}
          </>
        );
      }}
    </ConnectorDropdown>
  );

  const windowPicker = (
    <Dropdown
      align="bottom"
      opener={
        <PickerTrigger type="button">
          <Icon name="clock" size={12} />
          <Text variant="text-small-md" ellipsis>
            {selectedWindow.label}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </PickerTrigger>
      }
      sections={[
        {
          items: WINDOW_OPTIONS.map((option) => ({
            type: "button" as const,
            key: option.value,
            left: <Icon name="clock" size={16} color="var(--text-primary)" />,
            label: option.label,
            selected: option.value === selectedWindow.value,
            right:
              option.value === selectedWindow.value ? selectedCheck : undefined,
            onClick: () =>
              setParam("window", option.value, "connectors.window_change"),
          })),
        },
      ]}
    />
  );

  const stats: OverviewStat[] = [
    { label: "Requests", value: overview.requests, format: formatRequestCount },
    { label: "Errors", value: overview.errors, format: formatRequestCount },
    {
      label: "Error rate",
      value:
        overview.requests === 0
          ? 0
          : (overview.errors / overview.requests) * 100,
      format: overview.requests === 0 ? () => "—" : formatErrorRatePercent,
    },
  ];

  const refreshButton = (
    <RefreshButton onClick={() => setRefreshToken((token) => token + 1)} />
  );

  return (
    <MonitoringLayout
      headerItem={
        <MonitoringScopeSelector route={routes.monitoring.connectors} />
      }
    >
      <Content>
        <ConnectorsGrid>
          <ConnectorRequestVolumePanel
            volume={volume}
            series={volumeSeries.series}
            seriesKeyOf={volumeSeries.seriesKeyOf}
            stats={stats}
            nowDay={today0}
            placeholder={isEmpty}
            filters={
              isEmpty ? (
                <>
                  {windowPicker}
                  {refreshButton}
                </>
              ) : (
                <>
                  {connectorPicker}
                  {spacePicker}
                  {windowPicker}
                  {refreshButton}
                </>
              )
            }
          />
          <ConnectorVolumeBars
            bars={volumeBars}
            selectedConnectorId={effectiveConnectorId}
            placeholder={isEmpty}
            onToggle={(id) =>
              setParam(
                "connector",
                id === effectiveConnectorId ? null : id,
                "connectors.connector_filter_change"
              )
            }
            description={`Requests per connector ${selectedWindow.phrase}.`}
          />
          <ConnectorBreakdownPanel
            connectorSummaries={summaries}
            workflowSummaries={workflowSummaries}
            connectorMeta={connectorMeta}
            workflowsById={workflowsById}
            windowPhrase={selectedWindow.phrase}
            selectedConnectorId={effectiveConnectorId}
            unattributedSources={unattributedSources}
            placeholder={isEmpty}
            onClearConnector={() =>
              setParam("connector", null, "connectors.connector_filter_change")
            }
          />
        </ConnectorsGrid>
      </Content>
    </MonitoringLayout>
  );
}
